import { useEffect } from 'react';

import BarChart from '@App/components/BarChart/BarChart';

import { useTranslation } from '@utils/i18n/i18n.consumers';
import { HORIZONS } from '@App/pages/RiskDataPage/risk-data-page.constants';
import { RISK_DATA_KASUNGU } from '@App/pages/RiskDataPage/risk-data';
import { GLOSSARY_ITEMS } from '@App/components/Navbar/GlossarySearch/glossary-search.constants';

import DefinitionComponent from '@App/components/DefinitionComponent/DefinitionComponent';
import RiskDataStories from '../RiskDataStories/RiskDataStories';

import { getMaxValue } from '../risk-data-content.utils';

import './risk-data-extreme-wind.styl';

/**
 * @typedef {Object} RiskDataExtremeWindProps
 * @property {maplibregl.Map} map
 */

/**
 * @param {RiskDataExtremeWindProps} props
 */
function RiskDataExtremeWind({ map, selectedHorizon, selectedScenario }) {
  const { t } = useTranslation();

  useEffect(() => {
    if (map && selectedHorizon === HORIZONS[0]) {
      map.setLayoutProperty('baseline-wind-speed-layer', 'visibility', 'visible');
    }

    return () => {
      if (map) {
        map.setLayoutProperty('baseline-wind-speed-layer', 'visibility', 'none');
      }
    };
  }, [map, selectedHorizon]);

  useEffect(() => {
    if (map && selectedHorizon === HORIZONS[1]) {
      map.setLayoutProperty('future-wind-speed-layer', 'visibility', 'visible');
    }

    return () => {
      if (map) {
        map.setLayoutProperty('future-wind-speed-layer', 'visibility', 'none');
      }
    };
  }, [map, selectedHorizon]);

  return (
    <div className="risk-data-extreme-wind">
      <h3>
        {t('Introduction')}
      </h3>
      <p>
        {t('Wind related risks can be categorized as strong winds (storminess) or tropical cyclones. Tropical cyclones are rapidly rotating storms that develop over warm tropical oceans, from which they draw the energy to intensify. Tropical cyclones mostly affect the southern part of Malawi (coming from the coast of Mozambique).')}
      </p>
      <p>
        {t('Historical records show that only 5 tropical cyclones affected Malawi in the recent past. All of them had weakened to tropical depressions (maximum wind speed less than 17m/sec) by the time they landed in Malawi. However, the associated rainfall caused significant flooding in some cases. Cyclone Idai, in March 2019, caused devastating ')}
        <DefinitionComponent
          id={31}
          term="Floods"
          definition={GLOSSARY_ITEMS.find(item => item.id === 31)?.description}
          learnMoreUrl={GLOSSARY_ITEMS.find(item => item.id === 31)?.url}
          lowercase
        />
        {t(' that affected an estimated 975,600 people, submerged or washed away mature crops, and destroyed irrigation infrastructure. Tropical Storm Ana in 2022 caused damages estimated to be equivalent to 1.5–2.7 percent of Malawi’s GDP—most notably, requiring extensive and costly rehabilitation of the Kapichira Dam. Finally, cyclone Freddy in March 2023, was the worst on record for Malawi, having led to over 1,000 deaths and destroying 204,833 hectares of crops.')}
      </p>
    
      <p>
        <DefinitionComponent
          id={10}
          term="Climate change"
          definition={GLOSSARY_ITEMS.find(item => item.id === 10)?.description}
          learnMoreUrl={GLOSSARY_ITEMS.find(item => item.id === 10)?.url}
        />
        {t(' is projected to impact several aspects of tropical cyclone activity. Globally, the associated rainfall rates are projected to increase, as well as their intensities. While there is much uncertainty about the changes in the total number of storms globally, there is higher confidence that the number of the highest intensity storms such as Freddy, might increase.')}
      </p>
      <p>
        {t('The extreme wind speed (in m/s) associated with tropical cyclones that occurs on average once every 100 years is shown for Malawi <strong>on the map</strong> both for the baseline period (1980-2014) and the future short term period (2015-2050) for the high emission scenario. Extreme wind speeds larger than 18m/sec are only projected to reach the south of Malawi and not the Kasungu district. In the future scenario these wind speeds are projected to move farther north, but still only the Southern part of the country is projected to be affected.')}
      </p>
      <p>
        {t('Extreme wind speeds not associated with tropical cyclones in Kasungu specifically are not projected to change significantly. The figure shows the extreme wind speed that occurs on average once every 10 years in the baseline period, and its projected values in the future for Kasungu. The baseline values correspond to a breeze that can cause small trees to sway, and set large branches in trees in motion. Projected changes are very small, between 2 and 4%, indicating that damaging wind speeds are not projected to change significantly.')}
      </p>
      <BarChart
        data={RISK_DATA_KASUNGU['extreme-wind'][selectedScenario.id]}
        selectedItem={selectedHorizon.id}
        title={t('Extreme wind speed in the scenario of {{selectedScenario}}', { selectedScenario: t(selectedScenario.label).toLowerCase() })}
        xDataKey="horizon"
        yAxisLabel={t('m/s')}
        yDataKey="value"
        yDomain={[0, getMaxValue(RISK_DATA_KASUNGU['extreme-wind'])]}
      />
      <div className="how-does-this-affect-me__wrapper">
        <h3>{t('How does this affect me?')}</h3>
        <p>
          {t('Tropical cyclones are generally accompanied with heavy rains and severe ')}
          <DefinitionComponent
            id={31}
            term="Floods"
            definition={GLOSSARY_ITEMS.find(item => item.id === 31)?.description}
            learnMoreUrl={GLOSSARY_ITEMS.find(item => item.id === 31)?.url}
            lowercase
          />
          {t(' and can also cause landslides and storm surges. Coastal areas are the most vulnerable to cyclones and storms, especially people with limited mobility are at risk. Extreme winds and storminess causing large waves can impact fishing activities.')}
        </p>
      </div>
      <h3>
        {t('How can we adapt?')}
      </h3>
      <p>
        {t('Information supplied by national meteorological services and resilient healthcare infrastructure are crucial in vulnerable areas. Emergency response strategies and education can save lives. ')}
        <DefinitionComponent
          id={72}
          term="Weather"
          definition={GLOSSARY_ITEMS.find(item => item.id === 72)?.description}
          learnMoreUrl={GLOSSARY_ITEMS.find(item => item.id === 72)?.url}
        />
        {t(' warnings can advise people to avoid large water bodies during storms.')}
      </p>
      <RiskDataStories hazardCategories={['extreme-wind', 'tropical-cyclones']} />
    </div>
  );
}

export default RiskDataExtremeWind;